import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, CustomPaginator, GenericTable, GenericTableProps } from '@cdaas/reusable-components-daas';
import { AdminAssetListResponse, AdminAssetSearchResponse, FinanceSyncStatus } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { useTranslation } from 'react-i18next';

import { assetAdminClient } from '../../../../api-client/asset-admin-client';
import PageTemplate, { PageTemplateTranslations } from '../PageTemplate/PageTemplate';
import { DropdownMultiselect, OptionListOption } from '@gaiads/telia-react-component-library';
import { debounce } from 'lodash';

export default function DevicesPage() {
  const { t } = useTranslation('backOffice');
  const { t: tGlobal } = useTranslation('global');
  const pageTemplateTranslations: PageTemplateTranslations = {
    title: t('devicesPage.title'),
  };

  const optionList: OptionListOption[] = [
    {
      label: t('devicesPage.financeSyncStatus.error'),
      value: FinanceSyncStatus.Error,
      checked: true,
    },
    {
      label: t('devicesPage.financeSyncStatus.pending'),
      value: FinanceSyncStatus.Pending,
    },
    {
      label: t('devicesPage.financeSyncStatus.ok'),
      value: FinanceSyncStatus.Ok,
    },
  ];

  const [options, setOptions] = useState<OptionListOption[]>(optionList);

  const [devicesResponse, setDevicesResponse] = useState<AdminAssetListResponse | undefined>();
  const [devicesLoading, setDevicesLoading] = useState<GenericTableProps<AdminAssetSearchResponse>['loading']>('short');
  const [devicesApiError, setDevicesApiError] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const history = useHistory();

  const statusLabels = () => {
    const statusLabelList = options.filter(option => option.checked).map(option => option.label);
    return statusLabelList.length > 0 ? statusLabelList.join(', ') : t('devicesPage.financeSyncStatus.all');
  };

  const fetchDevices = async (page: number, financeSyncStatusList: FinanceSyncStatus[]): Promise<void> => {
    try {
      setDevicesLoading('short');
      const devices = (
        await assetAdminClient.adminListAssets(10, page, financeSyncStatusList.length > 0 ? financeSyncStatusList : undefined, undefined)
      ).data;
      setDevicesResponse(devices);
    } catch {
      setDevicesApiError(true);
    }
    setDevicesLoading('none');
  };

  const debouncedFetchDevices = useMemo(() => debounce(fetchDevices, 1000), []);

  useEffect(() => {
    debouncedFetchDevices(
      pageNumber,
      options.filter(option => option.checked).map(option => option.value as FinanceSyncStatus),
    );
  }, [debouncedFetchDevices, pageNumber, options]);

  const changeOptions = (newOptionList: OptionListOption[]) => {
    setPageNumber(0);
    setOptions(newOptionList);
  };

  return (
    <PageTemplate
      translations={pageTemplateTranslations}
      pageContent={
        <>
          <DropdownMultiselect
            label={t('devicesPage.financeSyncStatus.select')}
            onChange={(event, selectedOption) => {
              const newOptionList = options.map(option => {
                if (option.value === selectedOption.value) {
                  return { ...option, checked: !option.checked };
                } else {
                  return option;
                }
              });
              changeOptions(newOptionList);
            }}
            onClear={() => {
              changeOptions(optionList);
            }}
            options={options}
            disabled={devicesLoading !== 'none'}
          />
          <Card
            title={`${t('devicesPage.cardTitle')}: ${statusLabels()}`}
            wrapperDivCustomProps={{ backgroundColor: 'white', padding: { size: 'lg' }, borderRadius: { size: 'md' } }}
            tag={
              devicesResponse?.contents?.length
                ? { text: t('devicesPage.tagCheck') }
                : devicesLoading !== 'none'
                  ? { text: t('devicesPage.tagLoading'), tagProps: { backgroundColor: 'orange400' } }
                  : undefined
            }
            isInitiallyOpen={true}
          >
            <GenericTable
              isGenericTableInsideCard={true}
              noDataText={t('devicesPage.table.noDataText')}
              errorText={devicesApiError ? t('devicesPage.table.errorText') : undefined}
              loading={devicesLoading}
              inputData={devicesResponse?.contents}
              rowConfigs={[
                {
                  header: t('devicesPage.table.headerOrganization'),
                  key: 'organization.name',
                },
                {
                  header: t('devicesPage.table.headerName'),
                  key: 'product',
                  formatValue(product) {
                    return `${product.brand ? `${product.brand} ` : ''}${product.name}`;
                  },
                },
                {
                  header: t('devicesPage.table.headerUser'),
                  key: 'end_user',
                  formatValue(endUser) {
                    return `${
                      endUser.user
                        ? `${endUser.user.name ? endUser.user.name : tGlobal('inactiveUser')}`
                        : `${endUser.external_id ? endUser.external_id : tGlobal('companyDevice')}`
                    }`;
                  },
                },
              ]}
              buttonConfig={{
                name: t('devicesPage.table.rowButtonText'),
                buttonProps: {
                  iconName: 'chevron-right',
                  size: 'md',
                  iconOnRight: true,
                },
                buttonClickCallback: device => history.push(`/customer/${device.organization.id}/devices/${device.id}`),
              }}
            />
            {!!devicesResponse?.page_metadata && devicesLoading === 'none' && (
              <CustomPaginator
                activatePageInfoObject={devicesResponse.page_metadata}
                onPageChange={pageChangeInfo => setPageNumber(pageChangeInfo.nextChosenPageNumber)}
                paginatorStyleProps={{
                  pageSelectorProps: { centered: false },
                }}
              />
            )}
          </Card>
        </>
      }
    />
  );
}
